import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Star, Truck, Shield, Award } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Hero = () => {
  const { t } = useTranslation();

  return (
    <section className="relative bg-gradient-to-br from-pink-50 via-white to-green-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Content */}
          <div className="order-2 lg:order-1">
            <div className="mb-6">
              <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-[#95C11F] text-white mb-4">
                <Star className="w-4 h-4 mr-2" />
                {t('hero.badge')}
              </span>
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight">
                {t('hero.title')}
                <span className="bg-gradient-to-r from-[#4ECDC4] to-[#95C11F] bg-clip-text text-transparent">
                  {' '}{t('hero.titleHighlight')}{' '}
                </span>
                {t('hero.titleEnd')}
              </h1>
              <p className="text-xl text-gray-600 mt-6 leading-relaxed">
                {t('hero.subtitle')}
              </p>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center gap-6 mb-8">
              <div className="flex items-center text-yellow-500">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-current" />
                ))}
                <span className="ml-2 text-gray-700 font-medium">{t('hero.rating')}</span>
              </div>
              <div className="flex items-center text-gray-700">
                <Award className="w-5 h-5 text-[#4ECDC4] mr-2" />
                <span className="font-medium">{t('hero.certified')}</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Link to="/products" className="bg-gradient-to-r from-[#4ECDC4] to-[#95C11F] text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 mr-2" />
                {t('hero.buyNow')}
              </Link>
              <Link to="/products" className="border-2 border-[#4ECDC4] text-[#4ECDC4] px-8 py-4 rounded-full font-bold text-lg hover:bg-[#4ECDC4] hover:text-white transition-all duration-200 text-center">
                {t('hero.viewProducts')}
              </Link>
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex items-center text-gray-700">
                <Truck className="w-5 h-5 text-[#4ECDC4] mr-3" />
                <span className="font-medium">{t('hero.freeShipping')}</span>
              </div>
              <div className="flex items-center text-gray-700">
                <Shield className="w-5 h-5 text-[#4ECDC4] mr-3" />
                <span className="font-medium">{t('hero.naturalLabel')}</span>
              </div>
              <div className="flex items-center text-gray-700">
                <Award className="w-5 h-5 text-[#4ECDC4] mr-3" />
                <span className="font-medium">{t('hero.guarantee')}</span>
              </div>
            </div>
          </div>

          {/* Right Column - Hero Image */}
          <div className="order-1 lg:order-2 relative">
            <div className="relative">
              <img
                src="/images/hero-fitness.jpg"
                alt={t('hero.imageAlt')}
                className="w-full h-[500px] lg:h-[600px] object-cover rounded-2xl shadow-2xl"
              />

              {/* Floating Cards */}
              <div className="absolute -top-4 -left-4 bg-white p-4 rounded-xl shadow-lg">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                  <span className="text-sm font-bold text-gray-700">{t('hero.naturalIngredients')}</span>
                </div>
              </div>

              <div className="absolute -bottom-4 -right-4 bg-white p-4 rounded-xl shadow-lg">
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#4ECDC4]">15k+</div>
                  <div className="text-sm text-gray-600">{t('hero.satisfiedCustomers')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Background Decorations */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[#4ECDC4] to-transparent opacity-10 rounded-full -translate-y-32 translate-x-32"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-[#95C11F] to-transparent opacity-10 rounded-full translate-y-48 -translate-x-48"></div>
    </section>
  );
};

export default Hero;
